/**
 * SIMPLIFIED Add to Cart Functions
 * No universal tracking - just basic Shopify add to cart functionality
 */

/**
 * Simple add item to cart function
 * @param {string} variantId - The variant ID to add to cart
 * @param {number} quantity - The quantity to add (default: 1)
 * @param {HTMLElement} button - The button element that triggered the action
 * @returns {Promise} - Promise that resolves when the item is added to cart
 */
function addToCart(variantId, quantity = 1, button = null) {
  if (!variantId) {
    console.error('No variant ID provided');
    return Promise.reject('No variant ID provided');
  }

  // Convert and validate inputs
  const parsedVariantId = parseInt(variantId);
  const parsedQuantity = parseInt(quantity);
  
  if (isNaN(parsedVariantId) || parsedVariantId <= 0) {
    console.error('Invalid variant ID:', variantId);
    return Promise.reject('Invalid variant ID');
  }
  
  if (isNaN(parsedQuantity) || parsedQuantity <= 0) {
    console.error('Invalid quantity:', quantity);
    return Promise.reject('Invalid quantity');
  }

  console.log('Adding to cart - Variant ID:', parsedVariantId, 'Quantity:', parsedQuantity);

  // Store original content outside the promise chain
  let originalContent = '';
  
  // Show loading state if button is provided
  if (button) {
    originalContent = button.innerHTML;
    button.innerHTML = 'Adding...';
    button.style.opacity = '0.7';
    button.disabled = true;
  }

  const formData = {
    'items': [{
      'id': parsedVariantId,
      'quantity': parsedQuantity
    }]
  };
  return fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(formData)
  })
  .then(response => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  })  .then(data => {
    // SHOPIFY PIXEL MANAGER COMPLIANT TRACKING
    if (data.items && data.items.length > 0) {
      const addedItem = data.items[0];
      
      // 1. Fire Shopify cart change event for Pixel Manager (REQUIRED)
      try {
        document.dispatchEvent(new CustomEvent('shopify:cart:change'));
        console.log('✅ Shopify Pixel Manager: cart:change event fired');
      } catch (error) {
        console.error('❌ Shopify Pixel Manager cart:change error:', error);
      }
      
      // 2. Optional: Shopify Customer Events API for additional tracking
      if (window.Shopify?.customerEvents?.publish) {
        try {
          window.Shopify.customerEvents.publish('cart_line_added', {
            productVariantId: addedItem.variant_id,
            quantity: addedItem.quantity
          });
          console.log('✅ Shopify Customer Events: cart_line_added event published');
        } catch (error) {
          console.error('❌ Shopify Customer Events error:', error);
        }
      }
    }
    
    // Update cart count in header
    const headerCartCount = document.getElementById('headerCartCount') || document.querySelector('.cart-count');
    if (headerCartCount) {
      headerCartCount.textContent = data.item_count;
    }

    // Show success message
    if (button) {
      button.innerHTML = 'Added!';
      button.style.backgroundColor = '#00C851';
      button.style.opacity = '1';
      button.disabled = false;
      
      // Open cart drawer if functions exist
      if (typeof window.openCartDrawer === 'function') {
        window.openCartDrawer();
      }
      if (typeof window.refreshCartDrawer === 'function') {
        window.refreshCartDrawer();
      }
      
      // Reset button after delay
      setTimeout(() => {
        button.innerHTML = originalContent;
        button.style.backgroundColor = '';
      }, 2000);
    }
    
    return data;
  })
  .catch(error => {
    console.error('Error adding to cart:', error);
    
    if (button) {
      button.innerHTML = 'Error';
      button.style.backgroundColor = '#ff4444';
      button.style.opacity = '1';
      button.disabled = false;
      
      // Reset button after delay
      setTimeout(() => {
        button.innerHTML = originalContent;
        button.style.backgroundColor = '';
      }, 2000);
    }
    
    throw error;
  });
}

/**
 * Update cart item quantity
 */
function updateCartItemQuantity(key, quantity) {
  console.log('updateCartItemQuantity called with:', key, quantity);
  
  if (quantity < 1) {
    console.log('Quantity less than 1, aborting');
    return Promise.reject('Quantity must be at least 1');
  }
  
  const cartChangeUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) 
    ? window.Shopify.routes.root + 'cart/change.js' 
    : '/cart/change.js';
  
  console.log('Making request to:', cartChangeUrl);
  
  return fetch(cartChangeUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: key,
      quantity: quantity
    })
  })
  .then(response => {
    console.log('Cart change response status:', response.status);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  })  .then(cart => {
    console.log('Cart updated successfully:', cart);
    
    // Fire Shopify cart change event for Pixel Manager
    try {
      document.dispatchEvent(new CustomEvent('shopify:cart:change'));
      console.log('✅ Shopify Pixel Manager: cart:change event fired (quantity update)');
    } catch (error) {
      console.error('❌ Shopify Pixel Manager cart:change error:', error);
    }
    
    // Update cart count
    const headerCartCount = document.querySelector('.cart-count');
    if (headerCartCount) {
      headerCartCount.textContent = cart.item_count;
    }
    
    return cart;
  })
  .catch(error => {
    console.error('Error updating cart quantity:', error);
    throw error;
  });
}

/**
 * Remove item from cart
 */
function removeCartItem(key) {
  console.log('removeCartItem called with:', key);
  
  const cartChangeUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) 
    ? window.Shopify.routes.root + 'cart/change.js' 
    : '/cart/change.js';
  
  return fetch(cartChangeUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: key,
      quantity: 0
    })
  })
  .then(response => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  })  .then(cart => {
    console.log('Item removed successfully:', cart);
    
    // Fire Shopify cart change event for Pixel Manager (REQUIRED)
    try {
      document.dispatchEvent(new CustomEvent('shopify:cart:change'));
      console.log('✅ Shopify Pixel Manager: cart:change event fired (item removed)');
    } catch (error) {
      console.error('❌ Shopify Pixel Manager cart:change error:', error);
    }
    
    // Optional: Track removal with Shopify Customer Events API
    if (window.Shopify?.customerEvents?.publish) {
      try {
        window.Shopify.customerEvents.publish('cart_line_removed', {
          cartToken: cart.token
        });
        console.log('✅ Shopify Customer Events: cart_line_removed event published');
      } catch (error) {
        console.error('❌ Shopify Customer Events error:', error);
      }
    }
    
    // Update cart count
    const headerCartCount = document.querySelector('.cart-count');
    if (headerCartCount) {
      headerCartCount.textContent = cart.item_count;
    }
    
    return cart;
  })
  .catch(error => {
    console.error('Error removing cart item:', error);
    throw error;
  });
}

/**
 * Open cart drawer
 */
function openCartDrawer() {
  const cartDrawer = document.getElementById('cartDrawer');
  const cartOverlay = document.getElementById('cartOverlay');
  
  if (cartDrawer) {
    cartDrawer.classList.add('open');
    if (cartOverlay) {
      cartOverlay.classList.add('open');
    }
      // Track cart view with Shopify Customer Events API
    setTimeout(() => {
      if (window.Shopify?.customerEvents?.publish) {
        try {
          window.Shopify.customerEvents.publish('cart_viewed', {
            currency: window.Shopify?.currency?.active || 'USD'
          });
          console.log('✅ Shopify Customer Events: cart_viewed event published');
        } catch (error) {
          console.error('❌ Shopify Customer Events error:', error);
        }
      }
    }, 100); // Small delay to ensure cart content is loaded
  }
}

/**
 * Close cart drawer
 */
function closeCartDrawer() {
  const cartDrawer = document.getElementById('cartDrawer');
  const cartOverlay = document.getElementById('cartOverlay');
  
  if (cartDrawer) {
    cartDrawer.classList.remove('open');
    if (cartOverlay) {
      cartOverlay.classList.remove('open');
    }
  }
}

/**
 * Refresh the cart drawer contents
 */
function refreshCartDrawer() {
  const cartUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
    ? window.Shopify.routes.root + 'cart.js' 
    : '/cart.js';
  
  fetch(cartUrl)
    .then(response => response.json())    .then(cart => {
      const cartItems = document.getElementById('cartItems');
      const cartSubtotal = document.getElementById('cartSubtotal');
      const queueStatus = document.getElementById('queueCount');
      
      if (!cartItems || !cartSubtotal) {
        console.error('Required cart drawer elements not found!');
        return;
      }
      
      // Update queue status
      if (queueStatus) {
        queueStatus.textContent = `${cart.item_count} ITEMS`;
      }
      
      if (cart.item_count === 0) {
        cartItems.innerHTML = `
          <div class="empty-queue">
            <div class="empty-icon">[]</div>
            <div class="empty-message">QUEUE_EMPTY</div>
            <div class="empty-submessage">NO_ITEMS_IN_TRANSFER_QUEUE</div>
          </div>
        `;
        cartSubtotal.textContent = '$0.00';
        return;
      }
      
      let itemsHtml = '';
      let total = 0;
      
      cart.items.forEach((item, index) => {
        total += item.final_line_price;
        
        itemsHtml += `
          <div class="queue-item" data-item-key="${item.key}">
            <div class="item-image-container">
              <img src="${item.featured_image?.url || ''}" alt="${item.product_title}" class="item-image">
              <div class="item-scan-overlay"></div>
            </div>
            <div class="item-data">
              <div class="item-title">${item.product_title}</div>
              <div class="item-variant">${item.variant_title || ''}</div>
              <div class="item-price">$${(item.final_price / 100).toFixed(2)}</div>
              <div class="item-quantity">
                <button class="qty-btn qty-decrease" data-item-key="${item.key}" data-quantity="${item.quantity - 1}" ${item.quantity <= 1 ? 'disabled' : ''}>-</button>
                <span class="qty-display">${item.quantity}</span>
                <button class="qty-btn qty-increase" data-item-key="${item.key}" data-quantity="${item.quantity + 1}">+</button>
              </div>
            </div>
            <button class="remove-btn" data-item-key="${item.key}">REMOVE</button>
          </div>
        `;
      });
      
      cartItems.innerHTML = itemsHtml;
      cartSubtotal.textContent = `$${(total / 100).toFixed(2)}`;
      
      // Add event listeners to quantity buttons and remove buttons
      addCartItemEventListeners();
    })
    .catch(error => {
      console.error('Error refreshing cart:', error);
    });
}

/**
 * Add event listeners to cart item buttons
 */
function addCartItemEventListeners() {
  // Quantity decrease buttons
  document.querySelectorAll('.qty-decrease').forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();
      const itemKey = this.getAttribute('data-item-key');
      const quantity = parseInt(this.getAttribute('data-quantity'));
      
      if (quantity >= 1) {
        updateCartItemQuantity(itemKey, quantity).then(() => {
          refreshCartDrawer();
        });
      }
    });
  });

  // Quantity increase buttons
  document.querySelectorAll('.qty-increase').forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();
      const itemKey = this.getAttribute('data-item-key');
      const quantity = parseInt(this.getAttribute('data-quantity'));
      
      updateCartItemQuantity(itemKey, quantity).then(() => {
        refreshCartDrawer();
      });
    });
  });

  // Remove buttons
  document.querySelectorAll('.remove-btn').forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();
      const itemKey = this.getAttribute('data-item-key');
      
      removeCartItem(itemKey).then(() => {
        refreshCartDrawer();
      });
    });
  });
}

// Initialize cart functionality
document.addEventListener('DOMContentLoaded', function() {
  // NOTE: cart count is updated by cart-drawer.liquid's refreshCartDrawer() call — no extra fetch here.

  // Initialize cart drawer
  const cartButton = document.querySelector('.cart-button');
  if (cartButton) {
    cartButton.addEventListener('click', function(e) {
      e.preventDefault();
      openCartDrawer();
      refreshCartDrawer();
    });
  }

  // Close cart drawer when clicking outside
  document.addEventListener('click', function(event) {
    const cartDrawer = document.getElementById('cartDrawer');
    const cartButton = document.querySelector('.cart-button');
    
    if (cartDrawer && cartDrawer.classList.contains('open') && 
        !cartDrawer.contains(event.target) && 
        !cartButton.contains(event.target)) {
      closeCartDrawer();
    }
  });
});

// Make cart functions globally available
window.addToCart = addToCart;
window.removeCartItem = removeCartItem;
window.updateCartItemQuantity = updateCartItemQuantity;
window.openCartDrawer = openCartDrawer;
window.closeCartDrawer = closeCartDrawer;
window.refreshCartDrawer = refreshCartDrawer;

// Create cartFunctions object for easy access
window.cartFunctions = {
  addToCart: addToCart,
  removeCartItem: removeCartItem,
  updateCartItemQuantity: updateCartItemQuantity,
  openCartDrawer: openCartDrawer,
  closeCartDrawer: closeCartDrawer,
  refreshCartDrawer: refreshCartDrawer
};

console.log('Simple cart functions loaded');
